import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Star } from '@/components/Logo'

const PULL_THRESHOLD = 70
const MAX_PULL = 120

interface PullToRefreshProps {
  children: ReactNode
}

// Standalone PWAs on iOS don't get Safari's native pull-to-refresh (there's
// no browser chrome to drive it), so this reimplements it: a touch drag
// starting at scrollY 0 shows a brass star that spins faster the further
// you pull, and releasing past the threshold refetches every active query.
export function PullToRefresh({ children }: PullToRefreshProps) {
  const queryClient = useQueryClient()
  const [pullDistance, setPullDistance] = useState(0)
  const [dragging, setDragging] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const startYRef = useRef<number | null>(null)
  const pullingRef = useRef(false)

  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0]
      if (touch && window.scrollY === 0 && !refreshing) {
        startYRef.current = touch.clientY
        pullingRef.current = false
      } else {
        startYRef.current = null
      }
    }

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0]
      if (startYRef.current === null || refreshing || !touch) return
      const distance = touch.clientY - startYRef.current
      if (distance <= 0 || window.scrollY > 0) {
        pullingRef.current = false
        setDragging(false)
        setPullDistance(0)
        return
      }
      pullingRef.current = true
      setDragging(true)
      e.preventDefault()
      setPullDistance(Math.min(distance * 0.5, MAX_PULL))
    }

    const handleTouchEnd = async () => {
      setDragging(false)
      if (pullingRef.current && pullDistance > PULL_THRESHOLD) {
        setRefreshing(true)
        setPullDistance(PULL_THRESHOLD)
        await queryClient.refetchQueries({ type: 'active' })
        setRefreshing(false)
      }
      setPullDistance(0)
      startYRef.current = null
      pullingRef.current = false
    }

    window.addEventListener('touchstart', handleTouchStart, { passive: true })
    window.addEventListener('touchmove', handleTouchMove, { passive: false })
    window.addEventListener('touchend', handleTouchEnd)
    return () => {
      window.removeEventListener('touchstart', handleTouchStart)
      window.removeEventListener('touchmove', handleTouchMove)
      window.removeEventListener('touchend', handleTouchEnd)
    }
  }, [pullDistance, refreshing, queryClient])

  const active = pullDistance > 0 || refreshing
  const spinProgress = Math.min(pullDistance / PULL_THRESHOLD, 1)

  return (
    <>
      <div
        aria-hidden={!active}
        className="flex items-center justify-center overflow-hidden"
        style={{
          height: active ? Math.max(pullDistance, refreshing ? 48 : 0) : 0,
          transition: dragging ? 'none' : 'height 0.2s ease-out',
        }}
      >
        {active && (
          <div
            className={refreshing ? 'animate-spin' : ''}
            style={!refreshing ? { transform: `rotate(${spinProgress * 360}deg)` } : undefined}
          >
            <Star size={24} />
          </div>
        )}
      </div>
      {children}
    </>
  )
}
